import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Badge } from '../../components/ui/Badge';
import { MARKETING_URL } from '../../lib/constants';
// Public route — uses fetch directly (no auth token needed)

interface SnapshotDeliverable {
  id: string;
  title: string;
  status: 'not_started' | 'in_progress' | 'draft' | 'review' | 'approved' | 'exported';
}

interface FrameworkSnapshot {
  frameworkId: string;
  frameworkName: string;
  color: string;
  totalDeliverables: number;
  completedDeliverables: number;
  completionPercent: number;
  deliverables: SnapshotDeliverable[];
}

interface AuditorSnapshot {
  id: string;
  description: string;
  createdAt: string;
  companyName: string;
  totalDeliverables: number;
  completionPercent: number;
  frameworks: FrameworkSnapshot[];
}

const STATUS_CONFIG: Record<
  SnapshotDeliverable['status'],
  { label: string; variant: 'default' | 'brand' | 'success' | 'warning' | 'danger' | 'info' }
> = {
  not_started: { label: 'Not Started', variant: 'default' },
  in_progress: { label: 'In Progress', variant: 'brand' },
  draft: { label: 'Draft', variant: 'info' },
  review: { label: 'In Review', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  exported: { label: 'Exported', variant: 'success' },
};

export function AuditorView() {
  const { token } = useParams<{ token: string }>();
  const [snapshot, setSnapshot] = useState<AuditorSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetchSnapshot();
  }, [token]);

  async function fetchSnapshot() {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/auditor/${token}`);

      if (!response.ok) {
        if (response.status === 404 || response.status === 410) {
          setError('This auditor link is invalid or has expired.');
        } else {
          setError('Unable to load snapshot. Please try again later.');
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setSnapshot(data);
    } catch {
      setError('Unable to connect. Please check your internet connection and try again.');
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-dark">
        <AuditorHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
        <AuditorFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-dark">
        <AuditorHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 className="text-lg font-display font-semibold text-white mb-2">
              Link Unavailable
            </h2>
            <p className="text-slate-400 text-sm">{error}</p>
          </div>
        </div>
        <AuditorFooter />
      </div>
    );
  }

  if (!snapshot) return null;

  return (
    <div className="min-h-screen bg-surface-dark">
      <AuditorHeader />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Snapshot header */}
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            Compliance Snapshot
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {snapshot.companyName} — {formatDate(snapshot.createdAt)}
          </p>
          {snapshot.description && (
            <p className="text-slate-500 text-sm mt-1">{snapshot.description}</p>
          )}
        </div>

        {/* Overall summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-surface-mid rounded-xl p-5 border border-border">
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">
              Frameworks
            </p>
            <p className="text-2xl font-display font-bold text-white">
              {snapshot.frameworks.length}
            </p>
          </div>
          <div className="bg-surface-mid rounded-xl p-5 border border-border">
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">
              Total Deliverables
            </p>
            <p className="text-2xl font-display font-bold text-white">
              {snapshot.totalDeliverables}
            </p>
          </div>
          <div className="bg-surface-mid rounded-xl p-5 border border-border">
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">
              Overall Completion
            </p>
            <p className="text-2xl font-display font-bold text-white">
              {snapshot.completionPercent}%
            </p>
          </div>
        </div>

        {/* Framework cards */}
        <div className="space-y-4">
          {snapshot.frameworks.map((fw) => (
            <div
              key={fw.frameworkId}
              className="bg-surface-mid rounded-xl border border-border overflow-hidden"
            >
              {/* Framework header */}
              <div className="px-6 py-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: fw.color }}
                    />
                    <h2 className="text-lg font-display font-semibold text-white">
                      {fw.frameworkName}
                    </h2>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">
                      {fw.completionPercent}%
                    </p>
                    <p className="text-xs text-slate-500">
                      {fw.completedDeliverables} / {fw.totalDeliverables} deliverables
                    </p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-1.5 bg-surface-dark rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${fw.completionPercent}%`,
                      backgroundColor: fw.color,
                    }}
                  />
                </div>
              </div>

              {/* Deliverable list */}
              <div className="divide-y divide-border">
                {fw.deliverables.map((deliverable) => {
                  const statusConfig = STATUS_CONFIG[deliverable.status] || STATUS_CONFIG.not_started;

                  return (
                    <div
                      key={deliverable.id}
                      className="flex items-center justify-between px-6 py-3"
                    >
                      <p className="text-sm text-slate-300">{deliverable.title}</p>
                      <Badge variant={statusConfig.variant}>
                        {statusConfig.label}
                      </Badge>
                    </div>
                  );
                })}
                {fw.deliverables.length === 0 && (
                  <div className="px-6 py-4">
                    <p className="text-sm text-slate-500">
                      No deliverables in this framework.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      <AuditorFooter />
    </div>
  );
}

function AuditorHeader() {
  return (
    <header className="border-b border-border bg-surface-mid">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8 shrink-0">
            <rect x="2" y="2" width="28" height="28" rx="6" fill="#6c5ce7" />
            <path d="M10 19L16 13L22 19" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 24L16 18L22 24" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
          </svg>
          <span className="text-lg font-display font-bold">
            <span className="text-brand">Seed</span>
            <span className="text-slate-300">Governance</span>
          </span>
        </div>
      </div>
    </header>
  );
}

function AuditorFooter() {
  return (
    <footer className="border-t border-border mt-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Powered by{' '}
            <a
              href={MARKETING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-light hover:text-brand transition-colors"
            >
              SeedGovernance
            </a>
          </p>
          <p className="text-xs text-slate-500">
            Read-only compliance snapshot
          </p>
        </div>
      </div>
    </footer>
  );
}

function formatDate(dateStr: string) {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}
